import { useCallback, useEffect, useState } from "react";
import { Button, Input, Modal, Select, Tooltip } from "antd";
import { Copy, Download, Link2, Pencil, RefreshCw } from "lucide-react";
import useHttp from "hooks/useHttp";
import Table from "components/Table";
import Pagination from "pages/data-table-view/components/Pagination";
import Toast from "pages/data-table-view/components/Toast";
import {
  exportUtmLinks,
  fetchUtmLinks,
  generateUtmLinks,
  getUtmOptions,
  updateUtmLinkUrl,
} from "services/utm.service";

const OFFICIAL_UTM_LINK_COUNT = 69;
const EMPTY_OPTIONS = { sources: [], mediums: [], destinations: [] };

const normalizeOptions = (data) => ({
  sources: Array.isArray(data?.sources) ? data.sources : [],
  mediums: Array.isArray(data?.mediums) ? data.mediums : [],
  destinations: Array.isArray(data?.destinations) ? data.destinations : [],
});

const DESTINATION_LABELS = {
  vsl: "bv1",
  checkout: "innercircle",
};

const formatDestinationLabel = (value) => {
  const v = String(value ?? "");
  return DESTINATION_LABELS[v.toLowerCase()] ?? v;
};

function downloadCsv(rows) {
  const header = ["source", "medium", "campaign", "url", "destination", "createdAt"];
  const lines = [
    header.join(","),
    ...rows.map((row) =>
      header
        .map((key) => `"${String(row[key] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `utm-links-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function UtmLinksManager() {
  const api = useHttp();
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [filters, setFilters] = useState({ source: "", medium: "", destination: "", search: "" });
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "descending" });
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [editRow, setEditRow] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const loadOptions = useCallback(async () => {
    try {
      const data = await getUtmOptions(api);
      setOptions(normalizeOptions(data));
    } catch {
      setOptions(EMPTY_OPTIONS);
      setToast({ show: true, message: "Failed to load UTM options", type: "error" });
    }
  }, [api]);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const params = { page, limit };
      if (filters.source) params.source = filters.source;
      if (filters.medium) params.medium = filters.medium;
      if (filters.destination) params.destination = filters.destination;
      if (filters.search) params.search = filters.search;

      const data = await fetchUtmLinks(api, params);
      setRecords(Array.isArray(data?.records) ? data.records : []);
      setTotal(typeof data?.count === "number" ? data.count : 0);
    } catch {
      setRecords([]);
      setTotal(0);
      setListError("Failed to load UTM links");
      setToast({ show: true, message: "Failed to load UTM links", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [api, page, limit, filters]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleGenerateConfig = async () => {
    setGenerating(true);
    try {
      const data = await generateUtmLinks(api, { fromConfig: true });
      setToast({
        show: true,
        message: `Generated ${data.count} links from config`,
        type: "success",
      });
      loadLinks();
    } catch {
      setToast({ show: true, message: "Failed to generate config links", type: "error" });
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.source) params.source = filters.source;
      if (filters.medium) params.medium = filters.medium;
      if (filters.destination) params.destination = filters.destination;
      if (filters.search) params.search = filters.search;
      const data = await exportUtmLinks(api, params);
      downloadCsv(data.rows ?? []);
      setToast({ show: true, message: "Export complete", type: "success" });
    } catch {
      setToast({ show: true, message: "Export failed", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending",
    }));
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, total);

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setToast({ show: true, message: "Copied to clipboard", type: "success" });
    } catch {
      setToast({ show: true, message: "Copy failed", type: "error" });
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditUrl(row.url ?? "");
  };

  const closeEdit = () => {
    setEditRow(null);
    setEditUrl("");
  };

  const handleSaveUrl = async () => {
    const id = editRow?._id;
    const url = editUrl.trim();
    if (!id || !url) {
      setToast({ show: true, message: "URL is required", type: "error" });
      return;
    }
    setSaving(true);
    try {
      await updateUtmLinkUrl(api, id, url);
      setToast({ show: true, message: "URL updated", type: "success" });
      closeEdit();
      loadLinks();
    } catch {
      setToast({ show: true, message: "Failed to update URL", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "source", label: "Source", sortable: true },
    { key: "medium", label: "Medium", sortable: true },
    { key: "campaign", label: "Campaign", sortable: true },
    {
      key: "destination",
      label: "Destination",
      sortable: true,
      render: (row) => formatDestinationLabel(row.destination),
    },
    {
      key: "url",
      label: "URL",
      render: (row) => (
        <div className="flex items-center gap-2 max-w-md">
          <span className="truncate text-sm text-blue-700">{row.url}</span>
          <Tooltip title="Copy URL">
            <button
              type="button"
              onClick={() => copyUrl(row.url)}
              className="text-gray-500 hover:text-gray-800"
            >
              <Copy size={14} />
            </button>
          </Tooltip>
          <Tooltip title="Edit URL">
            <button
              type="button"
              onClick={() => openEdit(row)}
              className="text-gray-500 hover:text-gray-800"
            >
              <Pencil size={14} />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: true,
      render: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "-",
    },
  ];

  return (
    <div className="p-1">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-900">UTM Link Generator</h1>
        <div className="flex gap-2">
          <Button icon={<RefreshCw size={14} />} onClick={loadLinks}>
            Refresh
          </Button>
          <Button
            type="primary"
            loading={exporting}
            icon={<Download size={14} />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        <h2 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Link2 size={16} />
          Official BV1 UTM Links
        </h2>
        <p className="text-sm text-gray-600 mb-3">
          {OFFICIAL_UTM_LINK_COUNT} links from the BV1 UTM specification. Custom links are not
          supported.
        </p>
        <Button type="primary" loading={generating} onClick={handleGenerateConfig}>
          Sync All {OFFICIAL_UTM_LINK_COUNT} Official Links
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select
            allowClear
            placeholder="Filter source"
            value={filters.source || undefined}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, source: v ?? "" }));
            }}
            options={options.sources.map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            placeholder="Filter medium"
            value={filters.medium || undefined}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, medium: v ?? "" }));
            }}
            options={options.mediums.map((m) => ({ value: m, label: m }))}
          />
          <Select
            allowClear
            placeholder="Filter destination"
            value={filters.destination || undefined}
            onChange={(v) => {
              setPage(1);
              setFilters((f) => ({ ...f, destination: v ?? "" }));
            }}
            options={options.destinations.map((d) => ({
              value: d,
              label: formatDestinationLabel(d),
            }))}
          />
          <Input
            placeholder="Search campaign or URL"
            value={filters.search}
            onChange={(e) => {
              setPage(1);
              setFilters((f) => ({ ...f, search: e.target.value }));
            }}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={records}
        isLoading={loading}
        error={listError}
        sortConfig={sortConfig}
        onSort={handleSort}
        onRetry={loadLinks}
        emptyMessage="No UTM links yet"
      />

      {!loading && !listError && total > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          itemsPerPage={limit}
          showingFrom={showingFrom}
          showingTo={showingTo}
        />
      )}

      <Modal
        title="Edit UTM Link URL"
        open={!!editRow}
        onCancel={closeEdit}
        onOk={handleSaveUrl}
        okText="Save"
        confirmLoading={saving}
        destroyOnClose
      >
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            {[editRow?.source, editRow?.medium, editRow?.campaign]
              .filter(Boolean)
              .join(" / ")}
          </p>
          <Input.TextArea
            rows={4}
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            placeholder="https://example.com/?utm_source=..."
          />
        </div>
      </Modal>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: "", type: "" })}
        />
      )}
    </div>
  );
}
